import * as React from 'react';
import {useEffect} from 'react';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {DatePicker, DateTimePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {InputAdornment} from "@mui/material";
import {toKSTISOString, toKSTISOStringEnd} from "../commonmethod/CommonMethod";
import {useBodyScrollLock} from "../../hooks/useBodyScrollLock";
import "./simmsDatePicker.css";

function CustomToolbar(props) {
    const { value } = props;

    return (
        <div
            className=""
            style={{
                height : '110px',
                display: 'flex',
                flexDirection: 'column',
                padding: '18px', // MUI 기본 Padding
                paddingLeft : '2rem',
                backgroundColor: '#000000', // 툴바 배경색
                borderBottom: '1px solid #e0e0e0', // 하단 경계선
                fontFamily: 'Roboto, sans-serif',
            }}
        >
            <div>
                <p className={"datepicker-mui-year"}>
                    {value ? dayjs(value).format('YYYY') : '선택하세요'}
                </p>
                <p className={"datepicker-mui-month"}>
                    {value ? dayjs(value).format('MM월 DD일') : '-- / --'}
                </p>
            </div>
        </div>
    );
}

/**
 * constraint에 lessThan, largerThan으로 기간검색 가능
 * 기간검색으로 처리할 떄 상세페이지에서 constraint에 required:true옵션을 따로 넣어줘야함
 * <div>SimmsDatePicker</div>의 형태라 th_title에서 넘겨준 constraint.required를 받지 못함
 * */
export default function SimmsDatePicker({
    data,
    style,
    onChange,
    onUpdate,
    update,
    constraint,
    auth,
    readonly,
    placeHolder,
    time,
    /** 트리거 버튼 테마 — `ios` 사파리 스타일(기본) */
    theme = "ios",
}) {
    const [value, setValue] = React.useState(data ? dayjs(data) : null);
    const fromDateSelect = React.useRef(false);
    const [open, setOpen] = React.useState(false); // 팝업 열림 상태 관리
    const PickerComponent = time ? DateTimePicker : DatePicker;
    const pickerViews = time ? ['year', 'month', 'day', 'hours', 'minutes'] : ['year', 'month', 'day'];

    const format = time ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD";
    const displayFormat = time ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD';

    useEffect(() => {
        setValue(data ? dayjs(data) : null)
    }, [data]);

    useBodyScrollLock(open);

    // 등록, 수정 시 날짜선택
    const handleDateSelect = (newDate) => {
        fromDateSelect.current = true;

        if (!newDate) {
            onChange(null);
            setValue(null);
            return;
        }

        const updatedDate = dayjs(newDate).format(format);

        if(!update) {
            if(constraint?.largerThan){
                time ? onChange(updatedDate) : onChange(toKSTISOStringEnd(updatedDate));
            }else{
                time ? onChange(updatedDate) : onChange(toKSTISOString(updatedDate));
            }
        }
        setValue(newDate);
    };

    // 하나의 함수로 제어 시 clear, cancel 등 제어가 불안정해져 ok 누를 시 수정페이지에서만 업데이트 기능 동작
    const handleDateUpdate = (newDate) => {
        const updatedDate = newDate ? dayjs(newDate).format(format) : null;
        onUpdate(updatedDate);
    };

    const editable = !!auth && !readonly;
    const readonlySource = value ?? (data ? dayjs(data) : null);
    const readonlyText =
        readonlySource && dayjs(readonlySource).isValid()
            ? dayjs(readonlySource).format(displayFormat)
            : "";

    /** 보기 모드(auth 없음): KV 테이블과 동일한 읽기 전용 텍스트 */
    if (!editable) {
        return (
            <span className="asset-detail-kv-value" style={style}>
                {readonlyText || "\u00A0"}
            </span>
        );
    }

    return (
        // value : 값
        // format : 날짜포멧형식
        // reduceAnimations : 과도한 애니메이션 삭제
        // showDaysOutsideCurrentMonth : 현재 달 전후로 날짜표시
        // slots datepicker의 상단, 하단부 필드{toolbar, actionBar}
        // slotProps : 화면 input props
        <div
            className={[
                "simms-date-picker-cell",
                "simms-date-picker",
                theme === "ios" ? "simms-date-picker--ios simms-date-picker--kv-typography" : "",
                time ? "simms-date-picker--time" : "",
                open ? "is-open" : "",
                value ? "is-selected" : "",
            ].filter(Boolean).join(" ")}
        >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <PickerComponent
                {...(time
                    ? { ampm: false, views: pickerViews, timeSteps: { hours: 1, minutes: 1 } }
                    : { views: pickerViews })}
                value={value}
                format={format}
                enableAccessibleFieldDOMStructure={false}
                disableOpenPicker
                className="simms-date-picker-control"
                onChange={(newValue) => handleDateSelect(newValue)}
                reduceAnimations={true}
                showDaysOutsideCurrentMonth
                minDate={constraint?.largerThan}
                maxDate={constraint?.lessThan}
                closeOnSelect={false}
                open={open}
                // 등록페이지 : 날짜 클릭 시 닫힘(시, 분타입일 시 안닫히도록 수정)
                // 수정페이지 : 날짜 클릭해도 닫히지 않음
                onClose={() => {
                    if (fromDateSelect.current) {
                        fromDateSelect.current = false;
                        if (update) return;
                    }
                    setOpen(false);
                }}
                slots={{
                    // toolbar : (props) => <CustomToolbar {...props} />,
                    actionBar: (props) => (
                        <div className={"flex px-[26px] text-[13px] font-bold mb-[15px] datepicker-mui-action"}>
                            <p className={"cursor-pointer"} onClick={() => {
                                if(!constraint?.required){
                                    if(update){
                                        // eslint-disable-next-line no-restricted-globals
                                        if(confirm("데이터를 초기화하시겠습니까?")) { // 수정페이지에서 사용자가 "OK"를 누른 경우에만 실행
                                            handleDateSelect(null);
                                            handleDateUpdate(null);
                                        }
                                    }else{
                                        handleDateSelect(null);
                                    }
                                }else{
                                    alert("필수값이라 초기화할수 없습니다.")
                                }
                            }}
                            >CLEAR</p>
                            <p className={"cursor-pointer ml-auto"} onClick={() => {
                                    setValue(data ? dayjs(data) : null);
                                    props.onCancel();
                                }}
                            >CANCEL</p>
                            <p className="ml-3 cursor-pointer" onClick={() => {
                                if (update) {
                                    handleDateUpdate(value);
                                }
                                props.onAccept();
                                fromDateSelect.current = false;
                            }}>OK</p>
                        </div>
                    )
                }}
                slotProps={{
                    textField: {
                        size: 'small',
                        color: 'none',
                        onClick: () => {
                            if (!readonly && auth) setOpen(!open);
                        },
                        fullWidth: false,
                        sx: {
                            width: 'fit-content',
                            margin: 0,
                            display: 'inline-flex',
                            ...style,
                        },
                        InputProps: {
                            endAdornment: null,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarTodayIcon fontSize="inherit" />
                                </InputAdornment>
                            ),
                        },
                        inputProps: {
                            readOnly: true,
                            value: value
                                ? dayjs(value).format(displayFormat)
                                : placeHolder ?? '날짜를 선택하세요',
                        },
                    },
                    popper: {
                        className : "ignore-click",
                        sx: {
                            marginTop : "3px !important", // 팝업과 Input 사이에 여백 추가
                            marginBottom : "3px !important", // 팝업과 Input 사이에 여백 추가

                        },
                    },
                }}
            />
        </LocalizationProvider>
        </div>
    );
}
